/**
 * CentroAyudaConfigCards — Sección de Configuración del Centro de Ayuda
 * para la página Empresa. Renderiza 3 cards:
 *   1. Switch de activación del módulo.
 *   2. Configuración de Correos (hostname inbound + webhook URL + healthcheck).
 *   3. Configuración de Almacenamiento (Supabase o AWS S3).
 *
 * Las credenciales sensibles son write-only: el backend nunca devuelve
 * el valor real, solo enmascarado. Los campos del form vacíos NO actualizan.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Headset, Mail, HardDrive, RefreshCw, Loader2, Copy, CheckCircle2,
  AlertCircle, ShieldCheck, KeyRound, Lock, Database, Cloud, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const CentroAyudaConfigCards = ({ api, isAdmin, moduloActivo, onToggleModulo, themeColor }) => {
  const [loading, setLoading] = useState(true);
  const [savingCorreos, setSavingCorreos] = useState(false);
  const [savingStorage, setSavingStorage] = useState(false);
  const [testingStorage, setTestingStorage] = useState(false);
  const [regeneratingToken, setRegeneratingToken] = useState(false);
  const [verificandoHC, setVerificandoHC] = useState(false);

  const [correos, setCorreos] = useState({
    hostname_inbound: '',
    correo_inbound: '',
    healthcheck_activo: true,
    auto_reply_template: '',
    sendgrid_basic_auth_user: '',
    sendgrid_basic_auth_pass: '',
    sendgrid_basic_auth_user_masked: null,
    sendgrid_basic_auth_pass_masked: null,
    healthcheck_estado_actual: 'OK',
    healthcheck_ultimo_check: null,
    has_secret_path_token: false,
    webhook_url: null,
  });

  const [storage, setStorage] = useState({
    tipo: 'SUPABASE',
    s3_auth_mode: 'ACCESS_KEY',
    aws_access_key_id: '',
    aws_secret_key: '',
    aws_role_arn: '',
    aws_external_id: '',
    aws_region: 'us-east-1',
    s3_bucket: '',
    s3_root_path: '',
    supabase_bucket: 'tickets',
    aws_access_key_id_masked: null,
    has_aws_secret_key: false,
    ultimo_test_at: null,
    ultimo_test_ok: null,
    ultimo_test_error: null,
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get('/tickets/admin/config-correos'),
        api.get('/tickets/admin/config-storage'),
      ]);
      setCorreos((prev) => ({
        ...prev,
        ...c.data,
        // Limpiar campos write-only del form para evitar enviar valores fantasma
        sendgrid_basic_auth_user: '',
        sendgrid_basic_auth_pass: '',
      }));
      setStorage((prev) => ({
        ...prev,
        ...s.data,
        aws_access_key_id: '',
        aws_secret_key: '',
      }));
    } catch (e) {
      console.error('Error cargando config Centro de Ayuda:', e);
      if (e?.response?.status !== 403) {
        toast.error('Error cargando configuración del Centro de Ayuda');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (moduloActivo) {
      fetchAll();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [moduloActivo]);

  const guardarCorreos = async () => {
    setSavingCorreos(true);
    try {
      const payload = { ...correos };
      // No mandar al backend los campos enmascarados ni metadata
      delete payload.sendgrid_basic_auth_user_masked;
      delete payload.sendgrid_basic_auth_pass_masked;
      delete payload.has_secret_path_token;
      delete payload.webhook_url;
      delete payload.healthcheck_estado_actual;
      delete payload.healthcheck_ultimo_check;
      delete payload.healthcheck_ultimo_ok;
      delete payload.empresa_id;
      // Si los write-only están vacíos, no enviarlos
      if (!payload.sendgrid_basic_auth_user) delete payload.sendgrid_basic_auth_user;
      if (!payload.sendgrid_basic_auth_pass) delete payload.sendgrid_basic_auth_pass;
      const { data } = await api.put('/tickets/admin/config-correos', payload);
      setCorreos((prev) => ({
        ...prev,
        ...data,
        sendgrid_basic_auth_user: '',
        sendgrid_basic_auth_pass: '',
      }));
      toast.success('Configuración de correos guardada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando correos');
    } finally {
      setSavingCorreos(false);
    }
  };

  const regenerarToken = async () => {
    if (!window.confirm(
      '¿Regenerar el token del webhook?\n\nLa URL actual dejará de funcionar y deberás actualizarla en SendGrid.'
    )) return;
    setRegeneratingToken(true);
    try {
      const { data } = await api.post('/tickets/admin/config-correos/regenerar-token');
      setCorreos((prev) => ({ ...prev, ...data, sendgrid_basic_auth_user: '', sendgrid_basic_auth_pass: '' }));
      toast.success('Token regenerado. Actualiza la URL en SendGrid.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error regenerando token');
    } finally {
      setRegeneratingToken(false);
    }
  };

  const guardarStorage = async () => {
    setSavingStorage(true);
    try {
      const payload = { ...storage };
      delete payload.aws_access_key_id_masked;
      delete payload.has_aws_secret_key;
      delete payload.ultimo_test_at;
      delete payload.ultimo_test_ok;
      delete payload.ultimo_test_error;
      delete payload.empresa_id;
      if (!payload.aws_access_key_id) delete payload.aws_access_key_id;
      if (!payload.aws_secret_key) delete payload.aws_secret_key;
      const { data } = await api.put('/tickets/admin/config-storage', payload);
      setStorage((prev) => ({ ...prev, ...data, aws_access_key_id: '', aws_secret_key: '' }));
      toast.success('Configuración de almacenamiento guardada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando almacenamiento');
    } finally {
      setSavingStorage(false);
    }
  };

  const probarStorage = async () => {
    setTestingStorage(true);
    try {
      const { data } = await api.post('/tickets/admin/config-storage/test-conexion');
      setStorage((prev) => ({
        ...prev,
        ultimo_test_at: new Date().toISOString(),
        ultimo_test_ok: data.ok,
        ultimo_test_error: data.error,
      }));
      if (data.ok) {
        toast.success('Conexión exitosa');
      } else {
        toast.error(`Conexión falló: ${data.error || 'error desconocido'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error probando conexión');
    } finally {
      setTestingStorage(false);
    }
  };

  const verificarHealthcheck = async () => {
    setVerificandoHC(true);
    try {
      const { data } = await api.post('/tickets/admin/healthcheck/verificar-ahora');
      toast.success(`Healthcheck enviado (marker: ${data.marker_token?.slice(0, 12)}...)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando healthcheck');
    } finally {
      setVerificandoHC(false);
    }
  };

  const copiarWebhook = () => {
    if (correos.webhook_url) {
      navigator.clipboard.writeText(correos.webhook_url);
      toast.success('URL copiada al portapapeles');
    }
  };

  return (
    <>
      {/* CARD 1: Switch de activación del módulo */}
      <Card className="border-slate-200 lg:col-span-2" data-testid="card-centro-ayuda-activacion">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Headset className="w-5 h-5" style={{ color: themeColor }} />
            Centro de Ayuda (Sistema de Tickets)
          </CardTitle>
          <CardDescription>
            Active este módulo para que su empresa pueda gestionar tickets de atención al cliente vía email.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg border bg-slate-50">
            <div className="space-y-1">
              <Label className="text-base font-medium flex items-center gap-2">
                {moduloActivo ? (
                  <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Módulo activo</>
                ) : (
                  <><AlertCircle className="w-4 h-4 text-slate-400" /> Módulo desactivado</>
                )}
              </Label>
              <p className="text-xs text-slate-500">
                Al activarlo, aparecerá el grupo &quot;Centro de Ayuda&quot; en el sidebar con las opciones
                Tickets, Configuración y Estadísticas.
              </p>
            </div>
            <Switch
              checked={!!moduloActivo}
              onCheckedChange={onToggleModulo}
              disabled={!isAdmin}
              data-testid="switch-modulo-tickets"
            />
          </div>
        </CardContent>
      </Card>

      {/* Solo mostrar el resto si el módulo está activo */}
      {moduloActivo && (
        <>
          {/* CARD 2: Configuración de Correos del Centro de Ayuda */}
          <Card className="border-slate-200" data-testid="card-centro-ayuda-correos">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Mail className="w-5 h-5" />
                Configuración de Correos (Centro de Ayuda)
              </CardTitle>
              <CardDescription>
                Configure el dominio inbound y SendGrid Inbound Parse. El remitente usa el
                <strong> Correo Principal</strong> ya configurado en la tarjeta superior.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="hostname_inbound">Dominio Inbound (configurado en SendGrid)</Label>
                    <Input
                      id="hostname_inbound"
                      data-testid="hostname-inbound-input"
                      value={correos.hostname_inbound || ''}
                      onChange={(e) => setCorreos({ ...correos, hostname_inbound: e.target.value })}
                      disabled={!isAdmin}
                      placeholder="ej: tickets-dev.imagina.pe"
                      className="font-mono"
                    />
                    <p className="text-xs text-slate-500">
                      Subdominio con MX → mx.sendgrid.net. Debe estar verificado en SendGrid.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="correo_inbound">Correo Entrante (Inbound)</Label>
                    <Input
                      id="correo_inbound"
                      type="email"
                      data-testid="correo-inbound-input"
                      value={correos.correo_inbound || ''}
                      onChange={(e) => setCorreos({ ...correos, correo_inbound: e.target.value })}
                      disabled={!isAdmin}
                      placeholder="ej: sac@tickets-dev.imagina.pe"
                    />
                    <p className="text-xs text-slate-500">
                      Dirección a la que llegarán los correos de los clientes. Usado también para healthcheck.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="sg_user" className="flex items-center gap-1">
                        <KeyRound className="w-3.5 h-3.5" /> Basic Auth User
                      </Label>
                      <Input
                        id="sg_user"
                        data-testid="sg-user-input"
                        value={correos.sendgrid_basic_auth_user}
                        onChange={(e) => setCorreos({ ...correos, sendgrid_basic_auth_user: e.target.value })}
                        disabled={!isAdmin}
                        placeholder={correos.sendgrid_basic_auth_user_masked || 'sg_inbound'}
                      />
                      {correos.sendgrid_basic_auth_user_masked && !correos.sendgrid_basic_auth_user && (
                        <p className="text-xs text-slate-400">Actual: {correos.sendgrid_basic_auth_user_masked} (no editar para conservar)</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sg_pass" className="flex items-center gap-1">
                        <Lock className="w-3.5 h-3.5" /> Basic Auth Password
                      </Label>
                      <Input
                        id="sg_pass"
                        type="password"
                        data-testid="sg-pass-input"
                        value={correos.sendgrid_basic_auth_pass}
                        onChange={(e) => setCorreos({ ...correos, sendgrid_basic_auth_pass: e.target.value })}
                        disabled={!isAdmin}
                        placeholder={correos.sendgrid_basic_auth_pass_masked || ''}
                      />
                      {correos.sendgrid_basic_auth_pass_masked && !correos.sendgrid_basic_auth_pass && (
                        <p className="text-xs text-slate-400">Guardada. Deje vacío para conservar.</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="autoreply">Plantilla del acuse de recibo (HTML)</Label>
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            if (!window.confirm('¿Restaurar la plantilla por defecto? Se eliminará tu personalización y se usará el diseño profesional con el branding de la empresa.')) return;
                            try {
                              await api.post('/tickets/admin/config-correos/reset-template');
                              setCorreos({ ...correos, auto_reply_template: '' });
                              toast.success('Plantilla restaurada a la versión por defecto');
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'No se pudo restaurar');
                            }
                          }}
                          data-testid="btn-restore-template"
                          type="button"
                          className="text-xs h-7"
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-1" />
                          Restaurar default
                        </Button>
                      )}
                    </div>
                    <Textarea
                      id="autoreply"
                      data-testid="auto-reply-textarea"
                      rows={4}
                      value={correos.auto_reply_template || ''}
                      onChange={(e) => setCorreos({ ...correos, auto_reply_template: e.target.value })}
                      disabled={!isAdmin}
                      placeholder="Si dejas este campo vacío, se usará la plantilla por defecto con el branding de la empresa. Variables disponibles: {{ticket_codigo}}, {{cliente_nombre}}, {{empresa}}, {{asunto}}, {{color_primary}}"
                    />
                    <p className="text-xs text-slate-500">
                      Vacío = plantilla por defecto profesional con branding corporativo. Personalízala si necesitas un diseño específico.
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="space-y-1">
                      <Label className="text-sm flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-600" />
                        Healthcheck automático (9am y 2pm)
                      </Label>
                      <p className="text-xs text-slate-500">
                        Estado actual: <strong className={correos.healthcheck_estado_actual === 'OK' ? 'text-emerald-600' : 'text-rose-600'}>{correos.healthcheck_estado_actual}</strong>
                        {correos.healthcheck_ultimo_check && ` · último check: ${new Date(correos.healthcheck_ultimo_check).toLocaleString('es-PE')}`}
                      </p>
                    </div>
                    <Switch
                      checked={!!correos.healthcheck_activo}
                      onCheckedChange={(v) => setCorreos({ ...correos, healthcheck_activo: v })}
                      disabled={!isAdmin}
                      data-testid="switch-healthcheck-activo"
                    />
                  </div>

                  {correos.webhook_url && (
                    <Alert className="border-blue-200 bg-blue-50">
                      <AlertDescription className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-blue-900">URL del webhook para SendGrid</span>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={copiarWebhook} className="h-7 text-xs" data-testid="btn-copiar-webhook">
                              <Copy className="w-3 h-3 mr-1" /> Copiar
                            </Button>
                            {isAdmin && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={regenerarToken}
                                disabled={regeneratingToken}
                                className="h-7 text-xs"
                                data-testid="btn-regenerar-token"
                              >
                                {regeneratingToken ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                                Regenerar token
                              </Button>
                            )}
                          </div>
                        </div>
                        <code className="block text-[10px] font-mono break-all bg-white p-2 rounded border border-blue-200 max-h-24 overflow-auto">
                          {correos.webhook_url}
                        </code>
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="flex gap-2 pt-2">
                    {isAdmin && (
                      <Button onClick={guardarCorreos} disabled={savingCorreos} size="sm" data-testid="btn-guardar-correos-ca" style={{ backgroundColor: themeColor, color: 'white' }}>
                        {savingCorreos ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                        Guardar correos
                      </Button>
                    )}
                    {isAdmin && correos.healthcheck_activo && (
                      <Button onClick={verificarHealthcheck} disabled={verificandoHC} size="sm" variant="outline" data-testid="btn-verificar-ahora">
                        {verificandoHC ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
                        Verificar ahora
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* CARD 3: Configuración de Almacenamiento */}
          <Card className="border-slate-200" data-testid="card-centro-ayuda-storage">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <HardDrive className="w-5 h-5" />
                Configuración de Almacenamiento
              </CardTitle>
              <CardDescription>
                Dónde se guardan los adjuntos de tickets. Cada empresa elige Supabase Storage o AWS S3.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Tipo de almacenamiento</Label>
                    <Select
                      value={storage.tipo}
                      onValueChange={(v) => setStorage({ ...storage, tipo: v })}
                      disabled={!isAdmin}
                    >
                      <SelectTrigger data-testid="select-storage-tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SUPABASE">
                          <span className="flex items-center gap-2"><Database className="w-4 h-4" /> Supabase Storage</span>
                        </SelectItem>
                        <SelectItem value="S3">
                          <span className="flex items-center gap-2"><Cloud className="w-4 h-4" /> AWS S3</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {storage.tipo === 'SUPABASE' && (
                    <div className="space-y-2">
                      <Label>Nombre del bucket</Label>
                      <Input
                        data-testid="sb-bucket-input"
                        value={storage.supabase_bucket || ''}
                        onChange={(e) => setStorage({ ...storage, supabase_bucket: e.target.value })}
                        disabled={!isAdmin}
                        placeholder="tickets"
                      />
                    </div>
                  )}

                  {storage.tipo === 'S3' && (
                    <>
                      <div className="space-y-2">
                        <Label>Modo de autenticación AWS</Label>
                        <Select
                          value={storage.s3_auth_mode || 'ACCESS_KEY'}
                          onValueChange={(v) => setStorage({ ...storage, s3_auth_mode: v })}
                          disabled={!isAdmin}
                        >
                          <SelectTrigger data-testid="select-s3-auth-mode">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ACCESS_KEY">Access Key + Secret (simple)</SelectItem>
                            <SelectItem value="IAM_ROLE">IAM Role + External ID (recomendado)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {storage.s3_auth_mode === 'ACCESS_KEY' && (
                        <>
                          <div className="space-y-2">
                            <Label>AWS Access Key ID</Label>
                            <Input
                              data-testid="aws-access-key-input"
                              value={storage.aws_access_key_id}
                              onChange={(e) => setStorage({ ...storage, aws_access_key_id: e.target.value })}
                              disabled={!isAdmin}
                              placeholder={storage.aws_access_key_id_masked || 'AKIA...'}
                            />
                            {storage.aws_access_key_id_masked && !storage.aws_access_key_id && (
                              <p className="text-xs text-slate-400">Actual: {storage.aws_access_key_id_masked}. Deje vacío para conservar.</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>AWS Secret Access Key</Label>
                            <Input
                              type="password"
                              data-testid="aws-secret-key-input"
                              value={storage.aws_secret_key}
                              onChange={(e) => setStorage({ ...storage, aws_secret_key: e.target.value })}
                              disabled={!isAdmin}
                              placeholder={storage.has_aws_secret_key ? '••••••• (guardada)' : ''}
                            />
                          </div>
                        </>
                      )}

                      {storage.s3_auth_mode === 'IAM_ROLE' && (
                        <>
                          <div className="space-y-2">
                            <Label>Role ARN</Label>
                            <Input
                              data-testid="aws-role-arn-input"
                              value={storage.aws_role_arn || ''}
                              onChange={(e) => setStorage({ ...storage, aws_role_arn: e.target.value })}
                              disabled={!isAdmin}
                              placeholder="arn:aws:iam::123456789012:role/imagina-tickets"
                              className="font-mono text-xs"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>External ID</Label>
                            <Input
                              data-testid="aws-external-id-input"
                              value={storage.aws_external_id || '(se generará al guardar)'}
                              readOnly
                              className="font-mono text-xs bg-slate-50"
                            />
                            <p className="text-xs text-slate-500">Configure este valor en la condición trust del Role AWS.</p>
                          </div>
                        </>
                      )}

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>AWS Region</Label>
                          <Input
                            data-testid="aws-region-input"
                            value={storage.aws_region || ''}
                            onChange={(e) => setStorage({ ...storage, aws_region: e.target.value })}
                            disabled={!isAdmin}
                            placeholder="us-east-1"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>S3 Bucket</Label>
                          <Input
                            data-testid="s3-bucket-input"
                            value={storage.s3_bucket || ''}
                            onChange={(e) => setStorage({ ...storage, s3_bucket: e.target.value })}
                            disabled={!isAdmin}
                            placeholder="imagina-tickets"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Directorio raíz (prefijo)</Label>
                        <Input
                          data-testid="s3-root-path-input"
                          value={storage.s3_root_path || ''}
                          onChange={(e) => setStorage({ ...storage, s3_root_path: e.target.value })}
                          disabled={!isAdmin}
                          placeholder="empresa-acme/tickets"
                        />
                      </div>
                    </>
                  )}

                  {storage.ultimo_test_at && (
                    <Alert className={storage.ultimo_test_ok ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}>
                      <AlertDescription className="text-xs">
                        Último test: {new Date(storage.ultimo_test_at).toLocaleString('es-PE')} ·
                        <strong className={storage.ultimo_test_ok ? ' text-emerald-700' : ' text-rose-700'}>
                          {storage.ultimo_test_ok ? ' EXITOSO' : ' FALLIDO'}
                        </strong>
                        {storage.ultimo_test_error && <div className="mt-1 font-mono">{storage.ultimo_test_error}</div>}
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="flex gap-2 pt-2">
                    {isAdmin && (
                      <Button onClick={guardarStorage} disabled={savingStorage} size="sm" data-testid="btn-guardar-storage" style={{ backgroundColor: themeColor, color: 'white' }}>
                        {savingStorage ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                        Guardar almacenamiento
                      </Button>
                    )}
                    {isAdmin && (
                      <Button onClick={probarStorage} disabled={testingStorage} size="sm" variant="outline" data-testid="btn-probar-storage">
                        {testingStorage ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Cloud className="w-3.5 h-3.5 mr-1" />}
                        Probar conexión
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};

export default CentroAyudaConfigCards;
